import { CompanySubscription, UtilisationLevers } from "@/hooks/useSubscription";
import { ALERTS } from "@/constants/uiConstants";
import { ButtonType } from "@/contexts/DialogContext";

export interface SubscriptionData {
  status: 'ENTERPRISE' | 'ENTERPRISE_CANCELLED' | 'TRIAL' | 'TRIAL_ENDED' | 'NOT_ADDED' | 'CREDITS_LIMIT_REACHED';
  assignedDays: number;
  remainingDays: number;
  nextBillingDate: string;
  trialStartingNow?: boolean;
  dialog?: {
    title: string,
    desc: string,
    action: {label: string, id: string, type: ButtonType}[],
    icon: any,
    iconClass: string
  }
  tag?: {
    type: 'ENTERPRISE' | 'ENTERPRISE_CANCELLED' | 'TRIAL' | 'TRIAL_ENDED' | 'NOT_ADDED';
    label: 'Enterprise' | 'Enterprise Cancelled' | 'Trial' | 'Trial Ended' | '';
    state:  'success' | 'error' | 'warning' | '';
    name: string;
  } | null
}

export interface CreditsData{
    progress?: {
        credits?: number;
        usedCredits?: number;
        remainingCredits?: number;
        percentageOfUsage?: number;
        state:  'success' | 'error' | 'warning' | '';
    } | undefined;
}

export function getSubscriptionData(subscriptionData: {latest: CompanySubscription, utilization: UtilisationLevers}): SubscriptionData {
    if(!subscriptionData || !subscriptionData.latest){
        return {
            status: 'NOT_ADDED',
            assignedDays: 0,
            remainingDays: 0,
            nextBillingDate: '',
            tag: null
        }
    }
    const {latest, utilization} = subscriptionData;

    const remainingDays = getRemainingdaysInSubscription(latest);

    const assignedDays = latest.subscriptionPlan?.billingDays || 0;

    let dialog: SubscriptionData['dialog'];

    let {status, tag}: {status: SubscriptionData['status'], tag: SubscriptionData['tag']} = getTagData(latest, remainingDays);

    let remainingCredits, hasCredits = Object.keys(utilization).length;
    try{
        if(hasCredits && utilization?.ruleValue && utilization?.usageValue){
            remainingCredits = parseFloat(utilization.ruleValue) - parseFloat(utilization.usageValue);
        }
    }catch(e){
        console.log('Credits response incorrect', e);
    }
    if (status !== 'TRIAL_ENDED' && remainingCredits !== undefined && remainingCredits <= 0) {
        status = 'CREDITS_LIMIT_REACHED';
    }

    switch (status) {
        case 'TRIAL_ENDED':
            dialog = ALERTS.SUBSCRIPTION.TRIAL_EXPIRED;
            break;
        case 'CREDITS_LIMIT_REACHED':
            dialog = ALERTS.SUBSCRIPTION.CREDITS_EXPIRED;
            break;
        case 'ENTERPRISE_CANCELLED':
            dialog = ALERTS.SUBSCRIPTION.ENTERPRISE_EXPIRED;
            break;
        default:
            dialog = undefined;
            break;
    }

    const nextBillingDate = latest.nextBillingDate
        ? new Date(latest.nextBillingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';

    return {
        status,
        assignedDays,
        remainingDays,
        trialStartingNow: latest.trialStartingNow,
        nextBillingDate,
        tag,
        dialog
    };
}

export function getCreditsData(subscriptionData: {latest: CompanySubscription, utilization: UtilisationLevers}): CreditsData{
    const {latest, utilization} = subscriptionData;
    let { status }: {status: SubscriptionData['status']} = getTagData(latest, getRemainingdaysInSubscription(latest));
    let remainingCredits, credits, percentageOfUsage, usedCredits, hasCredits = Object.keys(utilization).length;
    try{
        if(hasCredits && utilization?.ruleValue && utilization?.usageValue){
            percentageOfUsage = utilization?.percentageOfUsage || 0;
            credits = parseFloat(utilization.ruleValue);
            remainingCredits = parseFloat(utilization.ruleValue) - parseFloat(utilization.usageValue);
            usedCredits = credits - remainingCredits;
        }
    }catch(e){
        console.log('Credits response incorrect', e);
    }
    if (status !== 'TRIAL_ENDED' && remainingCredits !== undefined && remainingCredits <= 0) {
        status = 'CREDITS_LIMIT_REACHED';
    }

    return {
        progress: hasCredits ? {
            credits,
            remainingCredits,
            usedCredits,
            percentageOfUsage,
            state: remainingCredits ? getTagState(remainingCredits, {warningDays: 25, errorDays: 0}) : 'error'
        } : undefined,
    }
}

function getTagData(latest: CompanySubscription, remainingDays: number): {status: SubscriptionData['status'], tag: SubscriptionData['tag']}{
    let tag: SubscriptionData['tag'];
    let status: SubscriptionData['status'];

    switch (latest.status) {
        case 'ACTIVE':
        case 'UPGRADED':
        case 'DOWNGRADED':
            status = 'ENTERPRISE';
            tag = { type: 'ENTERPRISE', label: 'Enterprise', name: 'Enterprise', state: getTagState(remainingDays) };
            break;
        case 'TRIAL':
            status = 'TRIAL';
            /* Users can be in 'TRAIL' state and trial may not begin yet. In which case, the 'nextBillingDate' would be 0 */
            tag = { type: 'TRIAL', label: 'Trial', name: 'Trial', state: (remainingDays === 0) ? "success" : getTagState(remainingDays) };
            break;
        case 'CANCELLED':
        case 'SUSPENDED':
        case 'CANCELLATION_REQUESTED':
            status = 'ENTERPRISE_CANCELLED';
            tag = { type: 'ENTERPRISE_CANCELLED', label: 'Enterprise Cancelled', name: 'Enterprise', state: 'error' };
            break;
        case 'TRIAL_ENDED':
            status = 'TRIAL_ENDED';
            tag = { type: 'TRIAL_ENDED', label: 'Trial Ended', name: 'Trial', state: 'error' };
            break;
        default:
            status = 'NOT_ADDED';
            tag = null;
            break;
    }

    return {status, tag};
}

function getRemainingdaysInSubscription(latest: CompanySubscription): number {
    if (!latest.nextBillingDate) {
        return 0;
    }

    const timeDiff = latest.nextBillingDate - Date.now();

    if (timeDiff <= 0) {
        return 0;
    }

    const daysRemaining = timeDiff / (1000 * 60 * 60 * 24);

    if(daysRemaining === 0){
        const hoursRemaining = Math.ceil(timeDiff / (1000 * 60 * 60));
        return hoursRemaining>0 ? hoursRemaining : 0;
    }

    return daysRemaining;
}

export function getTagState(remainingDays: number, config: {warningDays: number, errorDays: number} = {warningDays: 7, errorDays: 0}): 'success' | 'error' | 'warning' | ''{
    if(!remainingDays || remainingDays <= config.errorDays){
        return 'error';
    }else if(remainingDays <= config.warningDays){
        return 'warning';
    }
    return 'success';
}

export function trialNotStarted(subscriptionData?: SubscriptionData){
    return !subscriptionData?.status || (subscriptionData?.status === 'TRIAL' && !subscriptionData?.nextBillingDate)
}